import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const AdminSetup = ({ onSetupComplete }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    if (formData.email !== 'admin@lettherebe.ai') {
      toast.error('Unauthorized email address', {
        description: 'Only admin@lettherebe.ai can set up this account.'
      });
      return;
    }

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Admin account created successfully!');
        localStorage.setItem('creative_token', data.token);
        onSetupComplete();
        navigate('/creative-dashboard');
      } else {
        toast.error('Setup failed', {
          description: data.error || 'Unable to create admin account'
        });
      }
    } catch (error) {
      console.error('Setup error:', error);
      toast.error('Setup failed', {
        description: 'Unable to connect to server'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-6">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://customer-assets.emergentagent.com/job_creative-studio-146/artifacts/mfvy9mdh_sanjuanventures_A_3D_illustration_showing_four_people_two_men_55abb2cb-5ab1-4953-99f9-38c0fd640c4a_3.png"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>

      {/* Back to Site Button */}
      <a
        href="/"
        className="absolute top-6 left-6 z-20 text-white hover:text-pink-400 transition-colors flex items-center gap-1 text-sm font-medium drop-shadow-lg"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back
      </a>
      
      <div className="relative z-10 w-full max-w-md">
        <div className="mb-6 p-4 bg-white/90 backdrop-blur-sm border border-pink-200 rounded-lg">
          <p className="text-sm text-gray-800 font-semibold mb-1">Admin Only</p>
          <p className="text-xs text-gray-700">Only admin@lettherebe.ai can create this account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2 drop-shadow-lg">
              Email Address
            </label>
            <Input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="admin@lettherebe.ai"
              className="w-full placeholder:text-pink-400"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-2 drop-shadow-lg">
              Password
            </label>
            <Input
              id="password"
              type="password"
              required
              minLength={8}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Enter password (min 8 characters)"
              className="w-full placeholder:text-pink-400"
            />
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-900 mb-2 drop-shadow-lg">
              Confirm Password
            </label>
            <Input
              id="confirm-password"
              type="password"
              required
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Confirm your password"
              className="w-full placeholder:text-pink-400"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-violet-600 to-pink-600 text-white font-bold py-6 text-lg hover:shadow-lg transition-all duration-300"
          >
            {isLoading ? 'Creating Account...' : 'Create Admin Account'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminSetup;